import type { ExcelColumn } from '@/components/table/ExcelEditableTable'
import { createElement } from 'react'
import { Link } from 'react-router-dom'
import { StatusBadge } from '@/components/ui/StatusBadge'

export interface SalesPlanningRow extends Record<string, unknown> {
  id: string
  version: number
  sort_order?: number
  sales_order_id: string
  sales_order_code: string
  customer_name: string
  customer_code?: string
  buyer_name: string | null
  customer_id?: string | null
  buyer_id?: string | null
  variant_id?: string | null
  assembly_type?: 'assembled' | 'kd' | ''
  ista_level?: '1A' | '3A' | ''
  image_url?: string | null
  dim_unit?: 'inch' | 'mm'
  variant_code: string
  product_name: string
  item_code: string
  w: number | null
  d: number | null
  h: number | null
  color_name: string | null
  customer_po: string | null
  description: string | null
  material_type: string | null
  quantity: string | number
  containers_qty: string | number | null
  pcs_per_carton: number | null
  note: string | null
  line_status: 'draft' | 'sx' | 'cancelled'
  done: boolean
  production_month: string | null
  unit_price?: string | number | null
  total_amount?: number | null
  d_material_arrival: string | null
  d_white_done: string | null
  d_paint_done: string | null
  d_factory_completion: string | null
  d_customer_inspection: string | null
  unread_change_count?: number
  requested_ship_date: string | null
  wood_unit_text: string | null
  paint_unit_text: string | null
  uv_color: string | null
  work_hours: string | null
  overtime_hours: string | null
}

type Column = ExcelColumn<SalesPlanningRow>

export function canViewSalesSheet(roles: readonly string[]): boolean {
  return roles.includes('SALE') || roles.includes('ADMIN')
}

export function mapSalesPlanningRowsIfChanged(
  current: SalesPlanningRow[] | null,
  update: (row: SalesPlanningRow) => SalesPlanningRow,
): SalesPlanningRow[] | null {
  if (!current) return current
  let changed = false
  const next = current.map((row) => {
    const updated = update(row)
    if (updated !== row) changed = true
    return updated
  })
  return changed ? next : current
}

export function canEditSalesSheet(roles: readonly string[], permissions: readonly string[]): boolean {
  return roles.includes('SALE') && (permissions.includes('sales.write') || permissions.includes('*'))
}

const sale = (_key: keyof SalesPlanningRow & string) => (row: SalesPlanningRow) =>
  row.done || row.line_status === 'cancelled'
const plan = (row: SalesPlanningRow) => row.done || row.line_status !== 'sx'

export const PRODUCTION_PRINT_COLUMNS: Column[] = [
  { key: 'id', label: 'STT', width: 46, stickyLeft: 0, readOnly: true, formatter: (_v, _row, i) => String(i + 1) },
  { key: 'customer_id', label: 'KHÁCH HÀNG', width: 140, stickyLeft: 0, readOnly: sale('customer_id'), formatter: (_v, row) => row.customer_name },
  { key: 'buyer_id', label: 'Buyer', width: 178, stickyLeft: 0, readOnly: sale('buyer_id'), formatter: (_v, row) => row.buyer_name ?? '' },
  { key: 'item_code', label: 'HÌNH', width: 56, stickyLeft: 0, readOnly: true,
    formatter: (v, r) => String(v || r.variant_code || ''),
    renderCell: (v, r) => r.image_url
      ? createElement('img', { src: String(r.image_url), alt: String(v || r.variant_code || 'Ảnh sản phẩm'), className: 'mx-auto h-7 w-10 rounded object-cover' })
      : String(v || r.variant_code || '') },
  { key: 'variant_id', label: 'MÃ HÀNG', width: 110, stickyLeft: 0, readOnly: sale('variant_id'), formatter: (_v, row) => row.variant_code },
  { key: 'customer_po', label: 'K/H-PO', width: 150, readOnly: sale('customer_po'), headerTooltip: 'K/H-PO = PO do khách hàng cung cấp' },
  ...(['w', 'd', 'h'] as const).map((key) => ({ key, label: key.toUpperCase(), width: 56, type: 'number' as const, align: 'right' as const, readOnly: true })),
  { key: 'description', label: 'MÔ TẢ', width: 290, multiline: true, readOnly: sale('description') },
  { key: 'material_type', label: 'LOẠI NGUYÊN VẬT LIỆU', width: 170, multiline: true, readOnly: sale('material_type') },
  { key: 'color_name', label: 'Màu sơn', width: 118, readOnly: true },
  { key: 'quantity', label: 'SỐ LƯỢNG', width: 92, type: 'number', align: 'right', readOnly: sale('quantity') },
  { key: 'containers_qty', label: 'SL CONT', width: 62, type: 'number', align: 'right', readOnly: sale('containers_qty') },
  { key: 'wood_unit_text', label: 'Đơn vị Gia Công', width: 96, readOnly: plan },
  { key: 'paint_unit_text', label: 'CÔNG TY SƠN', width: 96, readOnly: plan },
  { key: 'uv_color', label: 'MÀU CẦN UV', width: 96, readOnly: plan },
  ...([
    ['d_material_arrival', 'NGÀY VỀ NVL', 76], ['work_hours', 'GIỜ', 54], ['overtime_hours', 'TĂNG CA', 62],
    ['d_white_done', 'Ngày xong hàng trắng', 84], ['d_paint_done', 'Ngày xong hàng sơn', 84],
    ['d_factory_completion', 'Ngày Hoàn Thành', 84], ['d_customer_inspection', 'FINAL', 90],
  ] as const).map(([key, label, width]) => ({ key, label, width, readOnly: plan(key as any) })) as Column[],
  { key: 'pcs_per_carton', label: 'Carton', width: 86, type: 'number', align: 'right', readOnly: sale('pcs_per_carton') },
  { key: 'note', label: 'Ghi chú', width: 220, multiline: true, readOnly: sale('note') },
]

export const SCREEN_ONLY_COLUMNS: Column[] = [
  { key: 'production_month', label: 'THÁNG SX', width: 96, readOnly: plan },
  { key: 'unit_price', label: 'ĐƠN GIÁ ($)', width: 100, type: 'currency', align: 'right', readOnly: sale('unit_price') },
  { key: 'total_amount', label: 'THÀNH TIỀN ($)', width: 116, type: 'currency', align: 'right', readOnly: true,
    formatter: (_v, row) => row.unit_price == null ? '' : (Number(row.quantity) * Number(row.unit_price)).toLocaleString('en-US') },
  { key: 'requested_ship_date', label: 'Khách YC hoàn thành', width: 92, readOnly: sale('requested_ship_date') },
  // Một cột trạng thái duy nhất: Hủy > Hoàn thành > Đang SX > Nháp (khớp các bộ lọc nhanh).
  { key: 'line_status', label: 'Trạng thái', width: 110, readOnly: true,
    formatter: (_value, row) => lineStatusLabel(row),
    renderCell: (_value, row) => createElement(StatusBadge, {
      tone: lineStatusTone(row),
      label: lineStatusLabel(row),
      showDot: true,
    }) },
  { key: 'sales_order_code', label: 'PO', width: 110, readOnly: true, headerTooltip: 'PO = mã đơn hàng nội bộ',
    renderCell: (_v, row) => createElement(Link, { className: 'text-sky-700 underline', to: `/sales/orders/${encodeURIComponent(row.sales_order_id)}` }, row.sales_order_code) },
]

export const SALES_EDITABLE_FIELDS = ['customer_id', 'buyer_id', 'variant_id', 'customer_po', 'description', 'material_type', 'quantity', 'containers_qty', 'unit_price', 'pcs_per_carton', 'note', 'requested_ship_date', 'assembly_type', 'ista_level'] as const
export const PLANNING_EDITABLE_FIELDS = ['production_month', 'wood_unit_text', 'paint_unit_text', 'uv_color', 'work_hours', 'overtime_hours', 'd_material_arrival', 'd_white_done', 'd_paint_done', 'd_factory_completion', 'd_customer_inspection'] as const

export function lineStatusLabel(row: { line_status?: string | null; done?: boolean | null }): string {
  if (row.line_status === 'cancelled') return 'Hủy'
  if (row.done) return 'Hoàn thành'
  return row.line_status === 'sx' ? 'Đang SX' : 'Nháp'
}

export function lineStatusTone(row: { line_status?: string | null; done?: boolean | null }): 'neutral' | 'info' | 'success' | 'warning' {
  if (row.line_status === 'cancelled') return 'neutral'
  if (row.done) return 'info'
  return row.line_status === 'sx' ? 'success' : 'warning'
}

export function filterCompletedSalesPlanningRows<T extends { done?: boolean | null }>(rows: T[], hideDone: boolean, showCompleted: boolean): T[] {
  return hideDone && !showCompleted ? rows.filter((row) => !row.done) : rows
}

export function salesPlanningTotals(rows: SalesPlanningRow[]) {
  return rows.filter((row) => row.line_status !== 'cancelled').reduce((sum, row) => ({
    qty: sum.qty + Number(row.quantity || 0),
    containers: sum.containers + Number(row.containers_qty || 0),
    amount: sum.amount + Number(row.total_amount || 0),
  }), { qty: 0, containers: 0, amount: 0 })
}

export function hideDoneStorageKey(grid: 'sales' | 'planning', userId: string | null | undefined): string {
  return `erp.grid.${grid}.hideDone.${userId ?? 'anonymous'}`
}

export const REQUIRED_SALES_PLANNING_COLUMNS = ['id', 'customer_id', 'variant_id', 'customer_po', 'quantity', 'assembly_type', 'ista_level', 'line_status'] as const

export function getDefaultHiddenSalesPlanningColumns(planning: boolean): string[] {
  return planning ? [] : [...PLANNING_EDITABLE_FIELDS]
}

export function parseAssemblyType(raw: string): string {
  return raw === 'Ráp chết' ? 'assembled' : raw === 'Tháo rời' ? 'kd' : raw
}

export function parseIstaLevel(raw: string): string {
  return raw === 'Thường (1A)' ? '1A' : raw === 'Ecom (3A)' ? '3A' : raw
}

export function createSheetEmptyRow(orderId = ''): SalesPlanningRow {
  return {
    id: `draft-${crypto.randomUUID()}`, version: 0, sales_order_id: orderId, sales_order_code: '', customer_name: '', buyer_name: null,
    variant_code: '', product_name: '', item_code: '', w: null, d: null, h: null, color_name: null, customer_po: null,
    description: null, material_type: null, quantity: '', containers_qty: null, pcs_per_carton: null, note: null,
    line_status: 'draft', done: false, production_month: null, d_material_arrival: null, d_white_done: null,
    d_paint_done: null, d_factory_completion: null, d_customer_inspection: null, requested_ship_date: null,
    wood_unit_text: null, paint_unit_text: null, uv_color: null, work_hours: null, overtime_hours: null,
    assembly_type: '', ista_level: '',
  }
}

/** Adds one fresh blank row for the one-new-PO creation flow. Repeated calls intentionally group rows into that PO. */
export function appendNewSalesOrderDraftRow(currentRows: SalesPlanningRow[] | null): SalesPlanningRow[] {
  return [...(currentRows ?? []), createSheetEmptyRow()]
}
